import { useLazyQuery } from '@apollo/client'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { ReactElement, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import EmptyState from 'components/v2/EmptyState'
import { NewVehiclesV2Step1 } from 'features/Vehicles/components/NewVehiclesV2Form/Step1'
import { NewVehicleV2Steps } from 'features/Vehicles/components/NewVehicleV2Steps'
import { ENewVehicleV2Step, IVehiclesV2FormValues } from 'features/Vehicles/types'
import { NewVehiclesV2Step2 } from 'features/Vehicles/components/NewVehiclesV2Form/Step2'
import { GET_VEHICLE_V2 } from 'features/Vehicles/queries-legacy'
import { GTTVehicle } from 'gql/types.generated'
import { VehiclesV2FormUtils } from 'features/Vehicles/utils'
import { filterEmptyValue } from 'utils/object'
import { NewVehiclesV2InsuranceStep } from 'features/Vehicles/components/NewVehiclesV2Form/NewVehiclesV2InsuranceStep'
import { NewVehiclesV2DocumentsStep } from 'features/Vehicles/components/NewVehiclesV2Form/NewVehiclesV2DocumentsStep'

const BreadcrumbWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

interface INewVehiclesV2Props {
  initialValues?: IVehiclesV2FormValues
  isEdit?: boolean
}

export const NewVehiclesV2 = ({ isEdit, initialValues }: INewVehiclesV2Props): ReactElement => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [getVehicle, { data: vehicleData, loading: vehicleLoading }] = useLazyQuery<{
    tvehicle: GTTVehicle
  }>(GET_VEHICLE_V2, {
    fetchPolicy: 'network-only'
  })
  const vehicleId = params.vehicleId || searchParams.get('vehicleId')
  useEffect(() => {
    const stepsFetchingVehicleData = [
      ENewVehicleV2Step.DETAILS.toString(),
      ENewVehicleV2Step.FEATURES.toString(),
      ENewVehicleV2Step.INSURANCE.toString()
    ]
    if (stepsFetchingVehicleData.includes(searchParams.get('step') || '')) {
      getVehicle({ variables: { input: { id: vehicleId } } })
    }
  }, [searchParams, getVehicle, vehicleId])

  let content = <></>
  const vehicle = vehicleData?.tvehicle ? VehiclesV2FormUtils.mapResponseToForm(vehicleData?.tvehicle) : initialValues
  const step1 = (
    <NewVehiclesV2Step1
      vehicle={vehicle}
      isEdit={isEdit}
      onSubmit={({ vehicleId }: { vehicleId: string }): void => {
        setSearchParams(
          filterEmptyValue({
            step: '2',
            vehicleId: searchParams.get('vehicleId') || vehicleId
          })
        )
      }}
      onCancel={(): void => {
        navigate('/vehicles')
      }}
    />
  )
  const step = searchParams.get('step') || '1'
  const stepNb = parseInt(step, 10)

  if (vehicleLoading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (!vehicleId) {
    content = step1
  } else {
    switch (stepNb) {
      case ENewVehicleV2Step.FEATURES:
        content = (
          <NewVehiclesV2Step2
            vehicle={vehicle}
            onSubmit={(): void => {
              setSearchParams(
                filterEmptyValue({
                  step: '3',
                  vehicleId: searchParams.get('vehicleId') || vehicleId
                })
              )
            }}
            onCancel={(): void => {
              setSearchParams(
                filterEmptyValue({
                  step: '1',
                  vehicleId: searchParams.get('vehicleId') || undefined
                })
              )
            }}
          />
        )
        break
      case ENewVehicleV2Step.INSURANCE:
        content = (
          <NewVehiclesV2InsuranceStep
            vehicle={vehicle}
            onSubmit={(): void => {
              setSearchParams(
                filterEmptyValue({
                  step: '4',
                  vehicleId: searchParams.get('vehicleId') || vehicleId
                })
              )
            }}
            onCancel={(): void => {
              setSearchParams(
                filterEmptyValue({
                  step: '2',
                  vehicleId: searchParams.get('vehicleId') || undefined
                })
              )
            }}
          />
        )
        break
      case ENewVehicleV2Step.DOCUMENTS:
        content = (
          <NewVehiclesV2DocumentsStep
            vehicle={vehicle}
            onSubmit={(): void => {
              navigate('/vehicles')
            }}
            onCancel={(): void => {
              setSearchParams(
                filterEmptyValue({
                  step: '3',
                  vehicleId: searchParams.get('vehicleId') || undefined
                })
              )
            }}
          />
        )
        break
      default:
        content = step1
        break
    }
  }

  return (
    <>
      <BreadcrumbWrapper>
        <NewVehicleV2Steps currentStep={stepNb} isEdit={isEdit} />
      </BreadcrumbWrapper>
      {content}
    </>
  )
}
